<template>
	<o-tour :ref="setup._tourOrderDetailsClient">
		<o-tour-step
			:title="$t('business.order.apiErrors.tour.orderDetails')"
			size="lg"
			hide-finish>
			<template #actions="{ next }">
				<o-button
					size="xs"
					outline
					color="info"
					@click="setup.openDiscussion()">
					{{ $t('common.order.tour.contactAgency') }}
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					{{ $t('common.actions.validate') }}
				</o-button>
			</template>

			<p v-html="$t('common.order.tour.orderCreatedDescription')"/>
			<ul>
				<li v-html="$t('common.order.tour.orderSummaryItem')"/>
				<li v-html="$t('common.order.tour.validatedCandidatesItem')"/>
				<li v-html="$t('common.order.tour.proposedCandidatesItem')"/>
			</ul>
			<o-alert color="info">
				<div v-html="$t('common.order.tour.orderModificationWarning')"/>
			</o-alert>
		</o-tour-step>

		<o-tour-step
			:title="$t('business.order.apiErrors.tour.orderSummary')"
			:target="setup.scrollToId.bind(setup, 'order-recap-card')"
			hide-finish>
			<p v-html="$t('common.order.tour.orderSummaryDetails')"/>
			<p v-html="$t('common.order.tour.orderStatusInfo')"/>
		</o-tour-step>

		<o-tour-step
			:title="$t('business.order.apiErrors.tour.goBack')"
			target="order-details__back"
			:next="setup.goToValidatedCandidates()"
			hide-finish>
			{{ $t('common.order.tour.clickHereToGoBack') }}
		</o-tour-step>

		<o-tour-step
			:title="$t('business.order.apiErrors.tour.validatedTempWorkers')"
			:target="setup.getTarget.bind(setup)"
			size="lg"
			:previous="setup.goToOrderDetails()"
			:next="{
				callback: () => Bus.emit('OrderDetailsClientSetup:changeTab', 'proposed'),
			}"
			hide-finish>
			<p v-html="$t('common.order.tour.availableSlotDescription')"/>
			<p v-html="$t('common.order.tour.profileValidationDescription')"/>
			<p>{{ $t('common.order.tour.exampleBelow') }}</p>
			<o-alert class="validated-candidate-sample-content">
				<div class="flex fd-c">
					<div class="validated-candidate-proposal__empty-card col">
						<o-icon font-icon="icon-interface-user-multiple"/>
						<p>{{ $t('common.order.tour.missingTempWorker') }} <br> 1 / 2</p>
					</div>
					<div class="col col-xs-2 validated-candidate-sample-content__arrows">
						<o-icon font-icon="icon-interface-arrows-button-down"/>
						<o-icon font-icon="icon-interface-arrows-button-down"/>
					</div>
					<div class="col no-pointer-events">
						<order-client-proposal-card
							validated
							:order-client-proposal="setup.sampleProposal"/>
					</div>
				</div>
			</o-alert>
		</o-tour-step>

		<o-tour-step
			:title="$t('business.order.apiErrors.tour.proposedCandidates')"
			:target="setup.getProposedCandidates.bind(setup)"
			:previous="{
				callback: () => Bus.emit('OrderDetailsClientSetup:changeTab', 'validated'),
			}"
			hide-finish>
			<p v-html="$t('common.order.tour.proposedCandidatesLocation')"/>
			<p v-html="$t('common.order.tour.proposedCandidatesProcess')"/>
		</o-tour-step>

		<o-tour-step
			v-if="setup.canCopy"
			:title="$t('business.order.apiErrors.tour.quickCreation')"
			size="lg"
			hide-finish
			target="order-details__copy">
			<p v-html="$t('common.order.tour.tempWorkTypes')"/>
			<p v-html="$t('common.order.tour.simplicitySpeed')"/>
			<p v-html="$t('common.order.tour.timeSavingFeature')"/>
			<p v-html="$t('common.order.tour.prefillDescription')"/>
		</o-tour-step>

		<o-tour-step
			:title="$t('business.order.apiErrors.tour.discussWithContact')"
			target="order-details__discussion">
			<span v-html="$t('common.order.tour.finalDiscussion')"/>

			<template #actions="{ previous }">
				<o-button
					size="xs"
					outline
					color="info"
					@click="setup.openDiscussion()">
					{{ $t('common.order.tour.contactAgency') }}
				</o-button>
				<o-button
					size="xs"
					outline
					@click="previous">
					{{ $t('common.actions.cancel') }}
				</o-button>
			</template>
		</o-tour-step>
	</o-tour>
</template>

<script setup lang="ts">
import OrderClientProposalCard from '@/components/order/OrderClientProposalCard.vue';
import { Bus } from '@orion.ui/orion';
import TourOrderDetailsClientSetup from './TourOrderDetailsClientSetup';
// #region Setup
import type { TourOrderDetailsClientEmits, TourOrderDetailsClientProps } from './TourOrderDetailsClientSetup';
const emits = defineEmits<TourOrderDetailsClientEmits>() as TourOrderDetailsClientEmits;
const props = withDefaults(defineProps<TourOrderDetailsClientProps>(), TourOrderDetailsClientSetup.defaultProps);
const setup = new TourOrderDetailsClientSetup(props, emits);
// #endregion
</script>

<style scoped lang="less">
.validated-candidate-sample-content {
	background-color: rgb(243 246 251 / 66%);

	&__arrows {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin: 1rem;
	}
}

.no-pointer-events {
	pointer-events: none;
}

.validated-candidate-proposal {
	margin-bottom: 3.1rem;

	&__progress-bar {
		width: 8.8rem;
	}

	&__empty-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 12rem;
		padding: 2.5rem;
		border-radius: 0.6rem;
		background: rgba(var(--rgb-grey), 0.1);

		.orion-icon {
			margin-bottom: 0.2rem;
			font-size: 3.1rem;
			color: var(--grey);
		}

		p {
			margin: unset;
			font-size: 0.75rem;
			font-style: italic;
			line-height: 1rem;
			color: var(--grey);
			text-align: center;
		}
	}
}
</style>
